import { createGlobalStyle, css } from 'styled-components';

import { zIndexes } from '@core/constant';
import { EColor, EFontWeight, ETypographyVariant, EZIndexName, WithThemeProps } from '@core/type';
import { getPaletteHandlers } from '@core/util';

const ICON_ANGEL_BASE64 =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIj4KICA8cGF0aCBkPSJNMTcuOTY3MSA4Ljk4MzY2TDExLjk4MzQgMTQuOTY3M0w1Ljk5OTc0IDguOTgzNjYiIHN0cm9rZT0iIzE5NDQ3NSIgc3Ryb2tlLXdpZHRoPSIxLjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K';

export const DatepickerGlobalStyle = createGlobalStyle(
  ({
    theme: {
      palette,
      typography: { fontWeight, ...restTypographyProps },
    },
  }: WithThemeProps) => {
    const { getColor, getTextColor, getBoxShadow, getBackgroundColor } =
      getPaletteHandlers(palette);

    const textPrimary = getTextColor(EColor.PRIMARY);
    const elevation2 = getBoxShadow(EColor.ELEVATION_2);
    const transparent = getColor(EColor.TRANSPARENT);
    const whiteR900 = getTextColor({ commonType: EColor.WHITE, intensity: EColor.R900 });
    const bgNeutralLight = getBackgroundColor({
      semanticType: EColor.NEUTRAL,
      variant: EColor.LIGHT,
    });

    // stylelint-disable no-descending-specificity, max-line-length
    return css`
      /* 1 Container */
      /* 1.1 Wrapper */

      .react-datepicker-wrapper {
        width: 100%;
      }

      .react-datepicker-popper {
        z-index: ${zIndexes[EZIndexName.DROPDOWN]};
      }

      .react-datepicker-popper[data-placement^='top'],
      .react-datepicker-popper[data-placement^='bottom'] {
        padding: 0;
      }

      .react-datepicker {
        border: none;
        width: 374px;
        height: auto;
        padding: 24px;
        color: ${textPrimary};
        box-shadow: ${elevation2};
      }

      /* 1.2 Container arrow */

      .react-datepicker__triangle {
        display: none;
      }

      /* 2 Header */
      /* 2.1 Container */

      .react-datepicker__header {
        padding: 0;
        background-color: ${transparent};
        border: none;
      }

      /* 2.2 Dropdowns */

      .react-datepicker__header__dropdown {
        display: flex;
        gap: 24px;
        margin-bottom: 24px;
      }

      .react-datepicker__month-dropdown-container,
      .react-datepicker__year-dropdown-container {
        position: relative;
        margin: 0;
      }

      .react-datepicker__month-dropdown,
      .react-datepicker__year-dropdown {
        top: 36px;
        left: 0;
        width: max-content;
        max-height: 250px;
        padding: 16px 0;
        box-shadow: ${elevation2};
        border: none;
        background-color: ${bgNeutralLight};
      }

      .react-datepicker__month-dropdown,
      .react-datepicker__year-dropdown--scrollable {
        height: auto;
        overflow-y: auto;
      }

      .react-datepicker__month-read-view,
      .react-datepicker__year-read-view {
        border: none;
        padding-right: 24px;
      }

      .react-datepicker__month-read-view[style^='visibility: hidden'],
      .react-datepicker__year-read-view[style^='visibility: hidden'] {
        visibility: visible !important;
      }

      .react-datepicker__month-read-view--down-arrow,
      .react-datepicker__year-read-view--down-arrow {
        top: calc(50% - 10px);
        right: 0;
        width: 20px;
        height: 20px;
        border: none;
        background-size: 20px;
        transform: none;
        background-image: url(${ICON_ANGEL_BASE64});
      }

      .react-datepicker__month-read-view[style^='visibility: hidden']
        .react-datepicker__month-read-view--down-arrow,
      .react-datepicker__year-read-view[style^='visibility: hidden']
        .react-datepicker__year-read-view--down-arrow {
        transform: rotate(180deg);
      }

      .react-datepicker__navigation--years {
        display: none;
      }

      .react-datepicker__month-read-view--selected-month,
      .react-datepicker__year-read-view--selected-year {
        ${restTypographyProps[ETypographyVariant.H5]};
        line-height: 36px;
      }

      .react-datepicker__month-option,
      .react-datepicker__year-option {
        ${restTypographyProps[ETypographyVariant.BODY2]};
        padding: 8px 24px;
        background-color: ${bgNeutralLight};
      }

      .react-datepicker__year-option .react-datepicker__navigation {
        display: block;
        margin-top: -4px;
        margin-bottom: -4px;
        top: 0;
        height: 20px;
        background-size: 20px;
        background-position: center;
        background-repeat: no-repeat;
        background-color: ${transparent};
        background-image: url(${ICON_ANGEL_BASE64});
      }

      .react-datepicker__year-option .react-datepicker__navigation--years-upcoming {
        transform: rotate(180deg);
      }

      .react-datepicker__month-option:hover,
      .react-datepicker__year-option:hover,
      .react-datepicker__month-option--selected_month,
      .react-datepicker__year-option--selected_year,
      .react-datepicker__month-option--selected_month:hover,
      .react-datepicker__year-option--selected_year:hover {
        background-color: ${getColor({ commonType: EColor.BLUE, intensity: EColor.R50 })};
      }

      .react-datepicker__month-option--selected_month .react-datepicker__month-option--selected,
      .react-datepicker__year-option--selected_year .react-datepicker__year-option--selected {
        display: none;
      }

      /* 2.3 Navigation buttons */

      .react-datepicker__navigation {
        top: 24px;
        width: 36px;
        height: 36px;
        border-radius: 8px;
        background-color: ${getBackgroundColor({
          semanticType: EColor.SECONDARY,
          variant: EColor.LIGHT,
        })};
      }

      .react-datepicker__navigation--previous {
        right: 76px;
        left: auto;
      }

      .react-datepicker__navigation--next {
        right: 24px;
      }

      .react-datepicker__navigation-icon {
        flex-shrink: 0;
        left: auto;
        right: auto;
        width: 36px;
        height: 36px;
      }

      .react-datepicker__navigation-icon:before {
        height: 24px;
        width: 24px;
        border: none;
        top: 6px;
        left: auto;
        transform: rotate(0);
        background-image: url(${ICON_ANGEL_BASE64});
      }

      .react-datepicker__navigation-icon--previous:before {
        transform: rotate(90deg);
        right: 6px;
      }

      .react-datepicker__navigation-icon--next:before {
        transform: rotate(270deg);
        left: 6px;
      }

      /* 2.4 Full date name */

      .react-datepicker__current-month {
        display: none;
      }

      /* 3 Body */
      /* 3.1 Month */

      .react-datepicker__month-container {
        width: 100%;
      }

      .react-datepicker__day-names,
      .react-datepicker__week {
        ${restTypographyProps[ETypographyVariant.BODY3]};
        margin: 0;
        display: flex;
        justify-content: space-around;
      }

      .react-datepicker__day-name {
        display: block;
        line-height: 42px;
        width: 42px;
        margin: 0;
        color: ${getTextColor(EColor.SECONDARY)};
      }

      .react-datepicker__month {
        margin: 0;
      }

      /* 3.2 Day */

      .react-datepicker__day {
        position: relative;
        margin: 0;
        line-height: 42px;
        width: 42px;
        outline: none;
        color: ${textPrimary};
        z-index: ${zIndexes[EZIndexName.DEFAULT]};
      }

      .react-datepicker__day:hover {
        background-color: transparent;
      }

      .react-datepicker__day:before {
        content: '';
        width: 32px;
        height: 32px;
        position: absolute;
        left: calc(50% - 16px);
        top: calc(50% - 16px);
        border-radius: 8px;
        z-index: ${zIndexes[EZIndexName.BACKGROUND]};
      }

      .react-datepicker__day:hover:not(
          .react-datepicker__day--disabled,
          .react-datepicker__day--selected,
          .react-datepicker__day--in-range
        ):before {
        color: ${textPrimary};
        background-color: ${getBackgroundColor(EColor.SECONDARY)};
      }

      .react-datepicker__day--outside-month {
        color: ${getTextColor(EColor.SECONDARY)};
      }

      [class*='react-datepicker__day--02'].react-datepicker__day--outside-month,
      [class*='react-datepicker__day--03'].react-datepicker__day--outside-month,
      .react-datepicker__day--disabled,
      .react-datepicker__day--excluded {
        color: ${getTextColor(EColor.NEUTRAL)};
        background-color: ${transparent};
      }

      .react-datepicker__day--today {
        font-weight: ${fontWeight[EFontWeight.REGULAR]};
      }

      .react-datepicker__day--selected,
      .react-datepicker__day--in-range,
      .react-datepicker__day--in-range.react-datepicker__day--outside-month {
        border: none;
        color: ${whiteR900};
        background-color: ${transparent};
        font-weight: ${fontWeight[EFontWeight.REGULAR]};
      }

      .react-datepicker__day.react-datepicker__day--selected:before,
      .react-datepicker__day.react-datepicker__day--in-range:before {
        background-color: ${getBackgroundColor(EColor.ACCENT)};
      }
    `;
  },
);
